// Customizable Area Start
import { BlockComponent } from "../../../framework/src/BlockComponent";
import { runEngine } from "../../../framework/src/RunEngine";
import { Message } from "../../../framework/src/Message";
import { IBlock } from "../../../framework/src/IBlock";
import MessageEnum, {
  getName,
} from "../../../framework/src/Messages/MessageEnum";
import { makeApiMessage } from "../../../components/src/common";

export interface IDriverLocation {
  latitude: number | string | null;
  longitude: number | string | null;
  updated_at: string | null;
  fresh: boolean;
  age_seconds: number | null;
  stale_after_seconds: number;
}

export interface IDriverAttendance {
  id?: number;
  work_date: string;
  status: string;
  checked_in_at: string | null;
  checked_out_at: string | null;
  on_break: boolean;
  break_started_at?: string | null;
  break_type?: string | null;
  worked_seconds: number;
  worked_hours?: number;
  break_seconds: number;
  adjusted?: boolean;
}

export interface IDriverTracker {
  id: string;
  type: string;
  attributes: {
    // NOTE: the top-level `id` above is the driver_profile id. Every other driver
    // endpoint — including the detail route — is keyed on account_id, so always
    // read it from here.
    account_id: number;
    vehicle_type: string | null;
    vehicle_plate: string | null;
    license_number: string | null;
    license_expiry: string | null;
    max_concurrent_requests: number;
    is_available: boolean;
    available_since: string | null;
    driver: {
      id: number;
      full_name: string | null;
      full_phone_number: string | null;
      email: string | null;
      activated: boolean;
    };
    location: IDriverLocation;
    attendance: IDriverAttendance;
    workload: {
      active_requests: number;
      max_concurrent: number;
      capacity_full: boolean;
      last_assigned_at: string | null;
    };
    stores: { id: number; store_name: string }[];
    tracker_state: string;
  };
}

export interface IDriverSummary {
  drivers: number;
  online: number;
  location_stale: number;
  by_state: {
    idle: number;
    on_job: number;
    on_break: number;
    no_signal: number;
    offline: number;
  };
  active_requests: number;
  checked_in: number;
  checked_out: number;
}

export interface ILiveLocation {
  account_id: number;
  full_name: string | null;
  latitude: number | string | null;
  longitude: number | string | null;
  updated_at: string | null;
  fresh: boolean;
  is_available: boolean;
  active_requests: number;
}

export interface IActiveRequest {
  id: number;
  request_number: string;
  status: string;
  status_label: string;
  leg: "pickup" | "delivery";
  customer_name: string | null;
  store_name: string | null;
  assigned_at: string | null;
  created_at: string;
}

export interface IAttendanceHistory {
  from: string;
  to: string;
  summary: {
    days_present: number;
    worked_seconds: number;
    worked_hours: number;
    break_seconds: number;
  };
  records: { data: { id: string; attributes: any }[] };
}

// Resolves in priority order — the first match wins, so an offline driver reads as
// `offline` even when their location is also stale.
export const TRACKER_STATES = [
  "offline",
  "no_signal",
  "on_break",
  "on_job",
  "idle",
];

export const ATTENDANCE_STATUSES = [
  "checked_in",
  "checked_out",
  "on_break",
  "not_checked_in",
];

// The board is heavy (attendance, workload and store joins); only /locations is
// meant to be called on a timer.
export const LOCATION_POLL_MS = 12000;

export const configJSON = require("./config");

export interface Props {
  navigation: any;
  id: string;
}

interface S {
  page: number;
  pageSize: number;
  totalCount: number;
  driverList: IDriverTracker[];
  summary: IDriverSummary | null;
  liveLocations: ILiveLocation[];
  isLoading: boolean;
  storeId: number;
  // 0 = driver list, 1 = live map
  activeTab: number;
  filterState: string;
  filterAttendance: string;
  filterAvailable: string;
  searchText: string;
  detailDialogOpen: boolean;
  detailData: IDriverTracker | null;
  detailHistory: IAttendanceHistory | null;
  detailRequests: IActiveRequest[];
  detailLoading: boolean;
  detailError: string;
  snackbarOpen: boolean;
  snackbarMessage: string;
  snackbarSeverity: "success" | "error";
}

interface SS {}

export default class DriverTrackerController extends BlockComponent<
  Props,
  S,
  SS
> {
  // Customizable Area Start
  getDriversMessageId: string = "";
  getLocationsMessageId: string = "";
  getDetailMessageId: string = "";
  locationTimer: ReturnType<typeof setInterval> | undefined;
  searchTimer: ReturnType<typeof setTimeout> | undefined;
  // Customizable Area End

  constructor(props: Props) {
    super(props);
    this.subScribedMessages = [
      getName(MessageEnum.RestAPIResponceMessage),
      getName(MessageEnum.LayoutDataMessage),
      getName(MessageEnum.BroadcastNavbarDataMessage),
    ];

    this.state = {
      page: 1,
      pageSize: 25,
      totalCount: 0,
      driverList: [],
      summary: null,
      liveLocations: [],
      isLoading: false,
      storeId: -1,
      activeTab: 0,
      filterState: "",
      filterAttendance: "",
      filterAvailable: "",
      searchText: "",
      detailDialogOpen: false,
      detailData: null,
      detailHistory: null,
      detailRequests: [],
      detailLoading: false,
      detailError: "",
      snackbarOpen: false,
      snackbarMessage: "",
      snackbarSeverity: "success",
    };

    this.receive = this.receive.bind(this);
    runEngine.attachBuildingBlock(this as IBlock, this.subScribedMessages);
  }

  async componentDidMount() {
    super.componentDidMount();
    this.getDriverList();
    this.startLocationPolling();
  }

  async componentWillUnmount() {
    this.stopLocationPolling();
    if (this.searchTimer) clearTimeout(this.searchTimer);
    // BlockComponent's unmount unsubscribes this block from the run engine.
    await super.componentWillUnmount();
  }

  async receive(from: string, message: Message) {
    if (message.id === getName(MessageEnum.BroadcastNavbarDataMessage)) {
      const recievedData = message.getData(
        getName(MessageEnum.BroadcastNavbarData)
      );
      if (recievedData?.singleStoreID) {
        const newStoreId = Number(recievedData.singleStoreID);
        this.setState({ storeId: newStoreId, page: 1 }, () => {
          this.getDriverList(1);
          this.getLiveLocations();
        });
      }
    }

    if (message.id === getName(MessageEnum.LayoutDataMessage)) {
      const recievedData = message.getData(
        getName(MessageEnum.LayoutMessageData)
      );
      const storeId =
        recievedData?.userContext?.user?.attributes?.employee_proifle?.data
          ?.attributes?.store_management?.store_id;
      if (storeId && this.state.storeId < 0) {
        this.setState({ storeId: Number(storeId) }, () => {
          this.getDriverList();
          this.getLiveLocations();
        });
      }
    }

    if (getName(MessageEnum.RestAPIResponceMessage) !== message.id) return;

    const requestId = message.getData(
      getName(MessageEnum.RestAPIResponceDataMessage)
    );
    const responseJson = message.getData(
      getName(MessageEnum.RestAPIResponceSuccessMessage)
    );

    if (requestId === this.getDriversMessageId) {
      this.handleDriversResponse(responseJson);
    }
    if (requestId === this.getLocationsMessageId) {
      this.handleLocationsResponse(responseJson);
    }
    if (requestId === this.getDetailMessageId) {
      this.handleDetailResponse(responseJson);
    }
  }

  // ---- responses ----------------------------------------------------------

  handleDriversResponse = (response: any) => {
    if (response?.data?.data) {
      this.setState({
        driverList: response.data.data,
        summary: response.summary ?? null,
        totalCount: response.meta?.total_count ?? 0,
        isLoading: false,
      });
      return;
    }

    this.setState({
      driverList: [],
      summary: null,
      totalCount: 0,
      isLoading: false,
      snackbarOpen: Boolean(response?.errors),
      snackbarMessage: this.readError(response, "Failed to load drivers"),
      snackbarSeverity: "error",
    });
  };

  handleLocationsResponse = (response: any) => {
    if (Array.isArray(response?.data)) {
      this.setState({ liveLocations: response.data });
    }
  };

  handleDetailResponse = (response: any) => {
    if (response?.data) {
      this.setState({
        detailData: response.data,
        detailHistory: response.attendance_history ?? null,
        detailRequests: response.active_requests ?? [],
        detailLoading: false,
        detailError: "",
      });
      return;
    }

    this.setState({
      detailLoading: false,
      detailError: this.readError(response, "Driver profile not found"),
    });
  };

  readError = (response: any, fallback: string) => {
    const errors = response?.errors;
    if (Array.isArray(errors) && errors.length) return String(errors[0]);
    if (typeof errors === "string") return errors;
    return fallback;
  };

  // ---- requests -----------------------------------------------------------

  buildListQuery = (page: number) => {
    const {
      pageSize,
      storeId,
      filterState,
      filterAttendance,
      filterAvailable,
      searchText,
    } = this.state;

    const params: string[] = [`page=${page}`, `per=${pageSize}`];
    if (storeId > 0) params.push(`store_id=${storeId}`);
    if (filterState) params.push(`state=${filterState}`);
    if (filterAttendance) params.push(`attendance_status=${filterAttendance}`);
    if (filterAvailable) params.push(`is_available=${filterAvailable}`);
    if (searchText.trim())
      params.push(`search=${encodeURIComponent(searchText.trim())}`);

    return params.join("&");
  };

  getDriverList = (page: number = this.state.page) => {
    this.setState({ isLoading: true });
    const requestMessage = makeApiMessage({
      url: `bx_block_order_management/admin_driver_trackers?${this.buildListQuery(
        page
      )}`,
      method: "GET",
    });
    this.getDriversMessageId = requestMessage.messageId;
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  getLiveLocations = () => {
    const { storeId } = this.state;
    // include_offline keeps a driver who has gone offline on the map with their
    // last known pin instead of making them vanish mid-shift.
    const params = [`include_offline=true`];
    if (storeId > 0) params.push(`store_id=${storeId}`);

    const requestMessage = makeApiMessage({
      url: `bx_block_order_management/admin_driver_trackers/locations?${params.join(
        "&"
      )}`,
      method: "GET",
    });
    this.getLocationsMessageId = requestMessage.messageId;
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  // :id here is the driver's account_id, not the driver_profile id.
  getDriverDetail = (accountId: number) => {
    this.setState({
      detailDialogOpen: true,
      detailLoading: true,
      detailData: null,
      detailHistory: null,
      detailRequests: [],
      detailError: "",
    });
    const requestMessage = makeApiMessage({
      url: `bx_block_order_management/admin_driver_trackers/${accountId}`,
      method: "GET",
    });
    this.getDetailMessageId = requestMessage.messageId;
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  startLocationPolling = () => {
    this.stopLocationPolling();
    this.getLiveLocations();
    this.locationTimer = setInterval(this.getLiveLocations, LOCATION_POLL_MS);
  };

  stopLocationPolling = () => {
    if (this.locationTimer) {
      clearInterval(this.locationTimer);
      this.locationTimer = undefined;
    }
  };

  // ---- handlers -----------------------------------------------------------

  handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    const page = newPage + 1;
    this.setState({ page }, () => this.getDriverList(page));
  };

  handleFilterChange = (
    key: "filterState" | "filterAttendance" | "filterAvailable",
    value: string
  ) => {
    this.setState({ ...this.state, [key]: value, page: 1 }, () =>
      this.getDriverList(1)
    );
  };

  handleSearchChange = (value: string) => {
    this.setState({ searchText: value, page: 1 });
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.getDriverList(1), 500);
  };

  handleClearFilters = () => {
    this.setState(
      {
        filterState: "",
        filterAttendance: "",
        filterAvailable: "",
        searchText: "",
        page: 1,
      },
      () => this.getDriverList(1)
    );
  };

  handleTabChange = (_event: React.ChangeEvent<{}>, activeTab: number) => {
    this.setState({ activeTab });
  };

  handleRefresh = () => {
    this.getDriverList();
    this.getLiveLocations();
  };

  handleCloseDetail = () => {
    this.setState({
      detailDialogOpen: false,
      detailData: null,
      detailHistory: null,
      detailRequests: [],
      detailError: "",
    });
  };

  handleSnackbarClose = () => {
    this.setState({ snackbarOpen: false });
  };

  // ---- derived ------------------------------------------------------------

  // The heartbeat only writes location, so merging the poll into the loaded rows
  // by account_id keeps everything else on the row intact between loads.
  getMergedDrivers = (): IDriverTracker[] => {
    const { driverList, liveLocations } = this.state;
    if (!liveLocations.length) return driverList;

    const byAccount = new Map<number, ILiveLocation>();
    liveLocations.forEach((live) => byAccount.set(live.account_id, live));

    return driverList.map((driver) => {
      const live = byAccount.get(driver.attributes.account_id);
      if (!live) return driver;

      return {
        ...driver,
        attributes: {
          ...driver.attributes,
          location: {
            ...driver.attributes.location,
            latitude: live.latitude,
            longitude: live.longitude,
            updated_at: live.updated_at,
            fresh: live.fresh,
            age_seconds: this.ageInSeconds(
              live.updated_at,
              driver.attributes.location.age_seconds
            ),
          },
          workload: {
            ...driver.attributes.workload,
            active_requests: live.active_requests,
          },
        },
      };
    });
  };

  ageInSeconds = (updatedAt: string | null, fallback: number | null) => {
    if (!updatedAt) return fallback;
    const parsed = new Date(updatedAt).getTime();
    if (Number.isNaN(parsed)) return fallback;
    return Math.max(0, Math.floor((Date.now() - parsed) / 1000));
  };

  // Coordinates are null only when a driver has never reported at all.
  getMappableDrivers = () =>
    this.getMergedDrivers().filter(
      (driver) =>
        driver.attributes.location.latitude !== null &&
        driver.attributes.location.longitude !== null
    );

  // ---- formatting ---------------------------------------------------------

  formatLabel = (value: string | null | undefined) => {
    if (!value) return "--";
    return value
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  formatDate = (dateStr: string | null) => {
    if (!dateStr) return "--";
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return "--";
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  formatDay = (dateStr: string | null) => {
    if (!dateStr) return "--";
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  formatDuration = (seconds: number | null | undefined) => {
    if (seconds === null || seconds === undefined) return "--";
    const total = Math.max(0, Math.floor(seconds));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m`;
    return `${total}s`;
  };

  formatAge = (location: IDriverLocation) => {
    if (location.latitude === null || location.longitude === null)
      return "Never reported";
    if (location.age_seconds === null) return "--";
    return `${this.formatDuration(location.age_seconds)} ago`;
  };

  openMapNavigation = (
    latitude: number | string,
    longitude: number | string
  ) => {
    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`,
      "_blank"
    );
  };
  // Customizable Area End
}
// Customizable Area End
